import { useEffect, useState } from 'react';
import { useTranslation } from '@/i18n';
import type { PanelBranding } from '@/utils/extensionHost';

interface TeamsSignInProps {
  onSignIn: () => void;
  error: string | null;
  busy: boolean;
  /** Where mot-r is hosted, for the header tagline and prompt. Defaults to Microsoft 365. */
  host?: 'teams' | 'extension' | 'outlook';
  /** The firm the sign-in goes to, when known (Chrome extension: from the Gmail address). */
  firmName?: string;
  /** The firm's logo, name and colours (Chrome extension / Outlook), when known. */
  branding?: PanelBranding | null;
}

const HOST_TEXT = {
  teams: {
    taglineKey: 'Auth:TaglineTeams',
    tagline: 'Legal workflows in Microsoft 365',
    promptKey: 'Auth:PromptTeams',
    prompt: 'Sign in once to use mot-r here in Microsoft Teams and Outlook.',
  },
  extension: {
    taglineKey: 'Auth:TaglineExtension',
    tagline: 'Legal workflows in Chrome',
    promptKey: 'Auth:PromptExtension',
    prompt: 'Sign in once to start mot-r jobs from this side panel.',
  },
  outlook: {
    taglineKey: 'Auth:TaglineOutlook',
    tagline: 'Legal workflows in Outlook',
    promptKey: 'Auth:PromptOutlook',
    prompt: 'Sign in once to start mot-r jobs from your email.',
  },
};

/**
 * Sign-in screen for the mot-r tab in Microsoft Teams, Outlook and the Microsoft 365 app.
 * Same look as the Outlook add-in / Gmail extension sign-in card (brand mark, lock hero,
 * navy primary button), so mot-r reads the same wherever Microsoft 365 hosts it.
 */
export const TeamsSignIn = ({ onSignIn, error, busy, host = 'teams', firmName, branding }: TeamsSignInProps) => {
  const { t } = useTranslation();
  const text = HOST_TEXT[host];
  const tagline = t(text.taglineKey, { defaultValue: text.tagline });
  const prompt = t(text.promptKey, { defaultValue: text.prompt });
  const [hover, setHover] = useState(false);
  // A logo that does not load (no logo uploaded) falls back to mot-r's mark and the firm's name.
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => setLogoFailed(false), [branding?.logoUrl]);
  const logoUrl = !logoFailed ? branding?.logoUrl : null;
  const title = branding?.name || 'mot-r';
  const buttonFirm = branding?.name || firmName || 'mot-r';
  const headerBackground = branding?.background || 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 60%, #33588a 100%)';
  const headerText = branding?.text || '#fff';
  const buttonBackground = branding?.background || '#1e3a5f';

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        backgroundColor: '#f5f7fa',
        fontFamily: "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        color: '#0f172a',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(15, 23, 42, .06), 0 1px 2px rgba(15, 23, 42, .04)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            background: headerBackground,
            color: headerText,
          }}
        >
          {logoUrl ? (
            <div style={{ minWidth: 0, flex: 1 }}>
              <img
                src={logoUrl}
                alt={title}
                onError={() => setLogoFailed(true)}
                style={{
                  display: 'block',
                  // Sized like the sidebar logo: a wide wordmark stays modest, a square mark small.
                  maxWidth: 'min(180px, 100%)',
                  maxHeight: '24px',
                  objectFit: 'contain',
                  filter: branding?.logoFilter || undefined,
                }}
              />
              <div style={{ marginTop: '6px', fontSize: '12px', opacity: 0.78 }}>{tagline}</div>
            </div>
          ) : (
            <>
              <div
                aria-hidden="true"
                style={{
                  width: '36px',
                  height: '36px',
                  flexShrink: 0,
                  borderRadius: '8px',
                  display: 'grid',
                  placeItems: 'center',
                  background: '#305CDE',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '19px',
                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.18), 0 4px 10px rgba(0,0,0,.18)',
                }}
              >
                {title === 'mot-r' ? 'm' : title.trim().charAt(0).toUpperCase()}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '16px', fontWeight: 700 }}>{title}</div>
                <div style={{ fontSize: '12px', opacity: 0.78 }}>{tagline}</div>
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '24px 20px 22px', textAlign: 'center' }}>
          <div
            aria-hidden="true"
            style={{
              width: '52px',
              height: '52px',
              margin: '0 auto',
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              background: '#eff6ff',
              color: '#305CDE',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          </div>
          <h1 style={{ fontSize: '16px', fontWeight: 700, margin: '12px 0 0' }}>{t('Auth:ConnectYourAccount', { defaultValue: 'Connect your account' })}</h1>
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#64748b' }}>
            {prompt}
          </p>
          <button
            type="button"
            onClick={onSignIn}
            disabled={busy}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
              width: '100%',
              minHeight: '44px',
              marginTop: '16px',
              border: 0,
              borderRadius: '8px',
              font: 'inherit',
              fontSize: '14px',
              fontWeight: 600,
              color: '#fff',
              background: buttonBackground,
              filter: hover && !busy ? 'brightness(1.25)' : undefined,
              boxShadow: '0 2px 6px rgba(30, 58, 95, .25)',
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy
              ? t('Auth:WaitingForSignIn', { defaultValue: 'Waiting for sign-in…' })
              : t('Auth:SignInToFirm', { defaultValue: 'Sign in to {{firm}}', firm: buttonFirm })}
          </button>
          {error && (
            <p role="alert" style={{ margin: '12px 0 0', fontSize: '13px', color: '#dc2626' }}>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
